import React from 'react';
import NavigationBar from '../navbar/NavigationBar';
import Footer from '../navbar/Footer';

const MissionCommunicationPage = () => {
  // Fonction pour obtenir le chemin des images
  const getImagePath = (fileName) => {
    if (!fileName || fileName.trim() === '') return null;
    
    const cleanName = fileName.trim();
    const extensions = ['.jpg', '.jpeg', '.png', '.webp', '.JPG', '.JPEG', '.PNG'];
    const hasExtension = extensions.some(ext => cleanName.endsWith(ext));
    
    if (hasExtension) {
      return `/assets/mission-communication/${cleanName}`;
    } else {
      return `/assets/mission-communication/${cleanName}.png`;
    }
  };

  // Données des provinces visitées
  const provinces = [
    { name: 'Butanyerera', date: '22-23 janvier 2026', image: 'butanyerera.jpeg' },
    { name: 'Buhumuza', date: '22-23 janvier 2026', image: 'buhumuza.jpeg' },
    { name: 'Burunga', date: '26-28 janvier 2026', image: 'burunga.jpeg' },
    { name: 'Gitega', date: '26-28 janvier 2026', image: 'gitega.jpeg' },
    { name: 'Bujumbura', date: '26-28 janvier 2026', image: 'bujumbura.jpeg' },
  ];

  // Défis identifiés
  const defis = [
    "Méconnaissance des plateformes de diffusion des données statistiques de l'INSBU",
    "Insuffisance de diffusion des résultats des enquêtes statistiques, malgré leur réalisation effective",
    "Absence ou faiblesse des budgets alloués à la communication statistique aux niveaux central, provincial et communal",
    "Manque de formation et de renforcement des capacités, particulièrement pour les nouveaux agents issus de la réforme administrative",
    "Faible accessibilité à l'Internet et aux outils numériques dans presque toutes les provinces",
    "Absence de supports de communication adaptés à la population (langue nationale, supports visuels, médias locaux)"
  ];

  // Opportunités
  const opportunites = [
    "Reconnaissance quasi unanime de l'INSBU comme source officielle et fiable des statistiques nationales",
    "Existence d'une bonne collaboration entre l'INSBU et certaines institutions sectorielles",
    "Potentiel du nouveau découpage administratif pour renforcer la désagrégation des données jusqu'au niveau local",
    "Intérêt manifesté par les médias, les universités et les partenaires techniques pour une meilleure valorisation des données",
    "Possibilités offertes par les outils numériques et les réseaux sociaux pour améliorer la diffusion"
  ];

  // Actions à court terme
  const actionsCourtTerme = [
    "Formaliser une unité ou un mécanisme fonctionnel dédié à la communication statistique au sein de l'INSBU",
    "Initier des actions de sensibilisation ciblées auprès des autorités administratives et des médias",
    "Renforcer la visibilité des plateformes statistiques existantes",
    "Diffuser systématiquement les principales publications statistiques en français et en kirundi",
    "Produire des supports simplifiés (notes de vulgarisation, infographies, communications numériques adaptées)"
  ];

  // Actions à moyen terme
  const actionsMoyenTerme = [
    "Mettre en œuvre un programme structuré de formation continue",
    "Accélérer la production et diffusion de statistiques désagrégées selon le nouveau découpage administratif",
    "Élaborer et publier régulièrement des annuaires statistiques provinciaux",
    "Diversifier les canaux de communication (réseaux sociaux institutionnels, plateformes interactives)",
    "Décentraliser la communication statistique jusqu'au niveau communal et zonal"
  ];

  // Actions à long terme
  const actionsLongTerme = [
    "Institutionnaliser les campagnes nationales de promotion de la culture statistique",
    "Intégrer davantage la statistique dans les programmes de formation académique et professionnelle",
    "Renforcer les partenariats avec les universités, les médias et les partenaires techniques et financiers",
    "Consolider la gouvernance des données (systèmes performants d'archivage, conservation, accessibilité)",
    "Assurer la durabilité du système de communication statistique"
  ];

  return (
    <div style={styles.container}>
      <NavigationBar />
      
      <main style={styles.main}>
        {/* En-tête avec titre et projet */}
        <header style={styles.header}>
          <div style={styles.projectBanner}>
            <span style={styles.projectTag}>Projet PROSTATBU</span>
            <span style={styles.financeTag}>Financé par la Banque mondiale</span>
          </div>
          
          <h4 style={styles.title}>
            Mission de descente sur terrain pour l'élaboration de la stratégie de communication du Système Statistique National
          </h4>
          
          <div style={styles.metaInfo}>
            <span style={styles.date}>22-28 janvier 2026</span>
            <span style={styles.location}>5 provinces du Burundi</span>
          </div>
        </header>

        {/* Section 1: Contexte et objectif */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>
            <span style={styles.sectionNumber}>01</span>
            Contexte et objectif de la mission
          </h2>
          
          <p style={styles.paragraph}>
            Dans le cadre de l'actualisation de la stratégie de communication du Système Statistique National (SSN), le service chargé de la Publication, Diffusion et Communication a effectué une série de descentes sur terrain afin de recueillir les perceptions, défis, opportunités et recommandations des acteurs institutionnels, administratifs, médiatiques et de la société civile.
          </p>
          
          <p style={styles.paragraph}>
            La mission visait principalement à évaluer le niveau de connaissance, d'utilisation et de satisfaction des parties prenantes vis-à-vis des produits statistiques du Système Statistique National (SSN), ainsi qu'à identifier les pistes d'amélioration de la communication statistique en lien avec la Vision Burundi 2040-2060 et en référence au modèle de la Stratégie de Communication Statistique des pays de la Communauté de l'Afrique de l'Est.
          </p>
        </section>

        {/* Section 2: Méthodologie et zone de couverture */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>
            <span style={styles.sectionNumber}>02</span>
            Méthodologie et zone de couverture
          </h2>
          
          <p style={styles.paragraph}>
            La mission s'est déroulée du 22 au 23 janvier 2026 dans les provinces de Butanyerera et Buhumuza, puis du 26 au 28 janvier 2026 dans les provinces de Burunga, Gitega et Bujumbura.
          </p>
          
          <p style={styles.paragraph}>
            La méthodologie adoptée a reposé sur des entretiens directs avec les autorités administratives provinciales et communales, les services techniques, les médias, les organisations de la société civile, les institutions d'enseignement supérieur ainsi que les partenaires techniques et financiers.
          </p>

          {/* Galerie des provinces visitées */}
          <div style={styles.provincesSection}>
            <h3 style={styles.subtitle}>Provinces visitées</h3>
            <div style={styles.provincesGrid}>
              {provinces.map((province, index) => (
                <div key={index} style={styles.provinceCard}>
                  <div style={styles.provinceImageContainer}>
                    <img 
                      src={getImagePath(province.image)} 
                      alt={`Province de ${province.name}`}
                      style={styles.provinceImage}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/assets/placeholder-province.png';
                      }}
                    />
                    <div style={styles.provinceOverlay}>
                      <span style={styles.provinceName}>{province.name}</span>
                    </div>
                  </div>
                  <p style={styles.provinceDate}>{province.date}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Section 3: Principaux constats transversaux */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>
            <span style={styles.sectionNumber}>03</span>
            Principaux constats transversaux
          </h2>
          
          <div style={styles.findingsContainer}>
            <div style={styles.findingItem}>
              <div style={styles.findingIcon}>🌐</div>
              <div>
                <h4 style={styles.findingTitle}>Faible consultation des plateformes statistiques</h4>
                <p style={styles.findingText}>
                  La consultation du site web et des plateformes statistiques de l'INSBU demeure globalement faible, voire inexistante pour une grande partie des acteurs rencontrés. Les données sont souvent recherchées via des moteurs de recherche généralistes.
                </p>
              </div>
            </div>

            <div style={styles.findingItem}>
              <div style={styles.findingIcon}>💰</div>
              <div>
                <h4 style={styles.findingTitle}>Déficit de moyens pour la communication</h4>
                <p style={styles.findingText}>
                  Absence de ligne budgétaire dédiée, insuffisance d'outils numériques, manque de connexion Internet stable et insuffisance de ressources humaines qualifiées, surtout dans le contexte du nouveau découpage administratif.
                </p>
              </div>
            </div>

            <div style={styles.findingItem}>
              <div style={styles.findingIcon}>🗺️</div>
              <div>
                <h4 style={styles.findingTitle}>Nouveau découpage administratif : défi et opportunité</h4>
                <p style={styles.findingText}>
                  Défi : inexpérience des nouveaux responsables. Opportunité : rapprochement des services statistiques de la population à travers les zones, communes et collines.
                </p>
              </div>
            </div>

            <div style={styles.findingItem}>
              <div style={styles.findingIcon}>📚</div>
              <div>
                <h4 style={styles.findingTitle}>Faible culture statistique</h4>
                <p style={styles.findingText}>
                  La culture statistique reste faible au sein de la population, ce qui limite l'appropriation et l'utilisation effective des données dans la planification, la prise de décision et le suivi-évaluation des politiques publiques.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Section 4: Défis spécifiques */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>
            <span style={styles.sectionNumber}>04</span>
            Défis spécifiques identifiés
          </h2>
          
          <div style={styles.challengesGrid}>
            {defis.map((defi, index) => (
              <div key={index} style={styles.challengeCard}>
                <div style={styles.checkmark}>✓</div>
                <p style={styles.challengeText}>{defi}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Section 5: Opportunités */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>
            <span style={styles.sectionNumber}>05</span>
            Opportunités pour la Vision Burundi 2040-2060
          </h2>
          
          <div style={styles.opportunitiesGrid}>
            {opportunites.map((opportunite, index) => (
              <div key={index} style={styles.opportunityCard}>
                <div style={styles.opportunityIcon}>🌟</div>
                <p style={styles.opportunityText}>{opportunite}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Section 6: Plan d'actions stratégiques */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>
            <span style={styles.sectionNumber}>06</span>
            Plan d'actions stratégiques pour la communication statistique
          </h2>

          {/* Actions à court terme */}
          <div style={styles.actionCategory}>
            <h3 style={styles.actionTitle}>
              <span style={styles.actionTimeframe}>Court terme</span>
              (0-12 mois)
            </h3>
            <ul style={styles.actionList}>
              {actionsCourtTerme.map((action, index) => (
                <li key={index} style={styles.actionItem}>{action}</li>
              ))}
            </ul>
          </div>

          {/* Actions à moyen terme */}
          <div style={styles.actionCategory}>
            <h3 style={styles.actionTitle}>
              <span style={styles.actionTimeframe}>Moyen terme</span>
              (1-3 ans)
            </h3>
            <ul style={styles.actionList}>
              {actionsMoyenTerme.map((action, index) => (
                <li key={index} style={styles.actionItem}>{action}</li>
              ))}
            </ul>
          </div>

          {/* Actions à long terme */}
          <div style={styles.actionCategory}>
            <h3 style={styles.actionTitle}>
              <span style={styles.actionTimeframe}>Long terme</span>
              (au-delà de 3 ans)
            </h3>
            <ul style={styles.actionList}>
              {actionsLongTerme.map((action, index) => (
                <li key={index} style={styles.actionItem}>{action}</li>
              ))}
            </ul>
          </div>
        </section>

        {/* Section 7: Satisfaction et conclusion */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>
            <span style={styles.sectionNumber}>07</span>
            Niveau de satisfaction et conclusion
          </h2>
          
          <div style={styles.satisfactionBox}>
            <div style={styles.satisfactionHeader}>
              <h3 style={styles.satisfactionTitle}>Niveau global de satisfaction</h3>
              <div style={styles.satisfactionLevel}>Moyennement satisfait à satisfait</div>
            </div>
            <p style={styles.satisfactionText}>
              Le niveau de satisfaction exprimé par les parties prenantes varie de moyennement satisfait à satisfait, avec quelques cas de forte satisfaction au niveau institutionnel national. Cette appréciation traduit une reconnaissance des efforts fournis par l'INSBU, tout en soulignant la nécessité d'intensifier les actions de communication, de moderniser les outils et de renforcer la proximité avec les utilisateurs des données.
            </p>
          </div>

          <div style={styles.conclusionBox}>
            <h3 style={styles.conclusionTitle}>Conclusion</h3>
            <p style={styles.conclusionText}>
              La descente sur terrain a permis de mettre en évidence l'importance stratégique de la communication statistique dans l'appropriation et l'utilisation des données du SSN. Les résultats de cette mission constituent une base essentielle pour l'élaboration d'une stratégie de communication inclusive, décentralisée et adaptée aux réalités locales, en cohérence avec les ambitions de la Vision Burundi 2040-2060 et du secrétariat des pays membres de la Communauté de l'Afrique de l'Est.
            </p>
          </div>
        </section>

        {/* Section 8: Implications pour la Direction Générale */}
        <section style={styles.implicationsSection}>
          <h2 style={styles.implicationsTitle}>
            <span style={styles.sectionNumber}>08</span>
            Implications pour la Direction Générale de l'INSBU
          </h2>
          
          <p style={styles.implicationsIntro}>
            La mise en œuvre effective du plan d'actions implique un engagement stratégique fort de la Direction Générale de l'INSBU, notamment par :
          </p>
          
          <div style={styles.implicationsGrid}>
            <div style={styles.implicationCard}>
              <div style={styles.implicationIcon}>💼</div>
              <h4>Allocation de ressources</h4>
              <p>Arbitrer en faveur de l'allocation de ressources humaines, matérielles et financières suffisantes</p>
            </div>
            
            <div style={styles.implicationCard}>
              <div style={styles.implicationIcon}>🏛️</div>
              <h4>Organisation institutionnelle</h4>
              <p>Valider l'organisation institutionnelle et les mécanismes de coordination interne et externe</p>
            </div>
            
            <div style={styles.implicationCard}>
              <div style={styles.implicationIcon}>🤝</div>
              <h4>Plaidoyer et partenariats</h4>
              <p>Renforcer le plaidoyer auprès des instances gouvernementales et des partenaires</p>
            </div>
            
            <div style={styles.implicationCard}>
              <div style={styles.implicationIcon}>🎯</div>
              <h4>Alignement stratégique</h4>
              <p>Veiller à l'alignement avec la Vision Burundi 2040-2060 et les orientations nationales</p>
            </div>
            
            <div style={styles.implicationCard}>
              <div style={styles.implicationIcon}>📈</div>
              <h4>Suivi et évaluation</h4>
              <p>Assurer le suivi et l'évaluation réguliers des actions mises en œuvre</p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: '#f5f5f5',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  main: {
    flex: 1,
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '20px',
    width: '100%',
  },
  header: {
    textAlign: 'center',
    marginBottom: '50px',
    paddingBottom: '25px',
    borderBottom: `2px solid #008D36`,
  },
  projectBanner: {
    display: 'flex',
    justifyContent: 'center',
    gap: '20px',
    marginBottom: '20px',
  },
  projectTag: {
    backgroundColor: '#E30613',
    color: 'white',
    padding: '8px 20px',
    borderRadius: '20px',
    fontSize: '0.9rem',
    fontWeight: 'bold',
  },
  financeTag: {
    backgroundColor: '#008D36',
    color: '#f5f5f5',
    padding: '8px 20px',
    borderRadius: '20px',
    fontSize: '0.9rem',
    fontWeight: 'bold',
  },
  title: {
    color: '#008D36',
    fontSize: '1.8rem',
    marginBottom: '15px',
    fontWeight: '600',
    lineHeight: 1.3,
    padding: '0 20px',
  },
  metaInfo: {
    display: 'flex',
    justifyContent: 'center',
    gap: '30px',
    marginBottom: '30px',
  },
  date: {
    padding: '8px 20px',
    backgroundColor: '#f0f0f0',
    borderRadius: '20px',
    fontSize: '1rem',
    fontWeight: '500',
  },
  location: {
    padding: '8px 20px',
    backgroundColor: '#f0f0f0',
    borderRadius: '20px',
    fontSize: '1rem',
    fontWeight: '500',
  },
  projectInfo: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    marginTop: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    textAlign: 'left',
  },
  projectTitle: {
    color: '#E30613',
    fontSize: '1.2rem',
    marginBottom: '15px',
    fontWeight: '600',
  },
  projectDetails: {
    color: '#666',
    fontSize: '0.95rem',
    lineHeight: 1.6,
  },
  section: {
    marginBottom: '60px',
    padding: '30px',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  },
  sectionTitle: {
    color: '#008D36',
    fontSize: '1.8rem',
    marginBottom: '25px',
    display: 'flex',
    alignItems: 'center',
    fontWeight: '600',
  },
  sectionNumber: {
    backgroundColor: '#E30613',
    color: 'white',
    padding: '5px 15px',
    borderRadius: '4px',
    marginRight: '15px',
    fontSize: '1.2rem',
    fontWeight: 'bold',
  },
  paragraph: {
    marginBottom: '20px',
    textAlign: 'justify',
    fontSize: '1.1rem',
    lineHeight: 1.7,
    color: '#333',
  },
  subtitle: {
    color: '#008D36',
    fontSize: '1.5rem',
    marginBottom: '25px',
    fontWeight: '600',
    textAlign: 'center',
  },
  provincesSection: {
    marginTop: '40px',
  },
  provincesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px',
    marginTop: '20px',
  },
  provinceCard: {
    backgroundColor: 'white',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 3px 10px rgba(0,0,0,0.08)',
    transition: 'transform 0.3s ease',
  },
  provinceImageContainer: {
    position: 'relative',
    height: '180px',
    overflow: 'hidden',
  },
  provinceImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  provinceOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
    padding: '10px',
  },
  provinceName: {
    color: 'white',
    fontSize: '1.2rem',
    fontWeight: 'bold',
  },
  provinceDate: {
    padding: '10px',
    textAlign: 'center',
    color: '#666',
    fontSize: '0.9rem',
  },
  findingsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '25px',
    marginTop: '20px',
  },
  findingItem: {
    display: 'flex',
    gap: '15px',
    alignItems: 'flex-start',
  },
  findingIcon: {
    fontSize: '2rem',
    flexShrink: 0,
  },
  findingTitle: {
    color: '#008D36',
    fontSize: '1.2rem',
    marginBottom: '8px',
    fontWeight: '600',
  },
  findingText: {
    color: '#666',
    lineHeight: 1.6,
    fontSize: '0.95rem',
  },
  challengesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '15px',
    marginTop: '20px',
  },
  challengeCard: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '15px',
    padding: '15px',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px',
    borderLeft: '4px solid #E30613',
  },
  checkmark: {
    color: '#008D36',
    fontSize: '1.2rem',
    fontWeight: 'bold',
  },
  challengeText: {
    color: '#333',
    lineHeight: 1.6,
    flex: 1,
  },
  opportunitiesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px',
    marginTop: '20px',
  },
  opportunityCard: {
    textAlign: 'center',
    padding: '20px',
    backgroundColor: '#f0f8ff',
    borderRadius: '8px',
    border: '1px solid #008D36',
  },
  opportunityIcon: {
    fontSize: '2rem',
    marginBottom: '10px',
  },
  opportunityText: {
    color: '#333',
    lineHeight: 1.6,
    fontSize: '0.95rem',
  },
  actionCategory: {
    marginBottom: '30px',
    padding: '20px',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px',
  },
  actionTitle: {
    color: '#008D36',
    fontSize: '1.3rem',
    marginBottom: '15px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontWeight: '600',
  },
  actionTimeframe: {
    backgroundColor: '#E30613',
    color: 'white',
    padding: '5px 15px',
    borderRadius: '4px',
    fontSize: '1rem',
  },
  actionList: {
    listStyleType: 'none',
    padding: 0,
  },
  actionItem: {
    marginBottom: '10px',
    paddingLeft: '20px',
    position: 'relative',
    lineHeight: 1.6,
  },
  actionItem: {
    marginBottom: '10px',
    paddingLeft: '20px',
    position: 'relative',
    lineHeight: 1.6,
  },
  actionItem: {
  '&:before': {
    content: '"•"',
    color: '#008D36',
    position: 'absolute',
    left: 0,
    fontSize: '1.2rem',
  }
},
  satisfactionBox: {
    backgroundColor: '#f0f8ff',
    padding: '25px',
    borderRadius: '8px',
    marginBottom: '30px',
    borderLeft: '4px solid #008D36',
  },
  satisfactionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '15px',
    flexWrap: 'wrap',
  },
  satisfactionTitle: {
    color: '#008D36',
    fontSize: '1.3rem',
    fontWeight: '600',
  },
  satisfactionLevel: {
    backgroundColor: '#008D36',
    color: '#f5f5f5',
    padding: '8px 20px',
    borderRadius: '20px',
    fontWeight: 'bold',
  },
  satisfactionText: {
    color: '#333',
    lineHeight: 1.7,
    fontSize: '1.05rem',
  },
  conclusionBox: {
    backgroundColor: '#f9f9f9',
    padding: '25px',
    borderRadius: '8px',
    borderTop: '4px solid #E30613',
  },
  conclusionTitle: {
    color: '#E30613',
    fontSize: '1.3rem',
    marginBottom: '15px',
    fontWeight: '600',
  },
  conclusionText: {
    color: '#333',
    lineHeight: 1.7,
    fontSize: '1.05rem',
    textAlign: 'justify',
  },
  implicationsSection: {
    marginBottom: '60px',
    padding: '30px',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    border: '2px solid #008D36',
  },
  implicationsTitle: {
    color: '#008D36',
    fontSize: '1.8rem',
    marginBottom: '25px',
    display: 'flex',
    alignItems: 'center',
    fontWeight: '600',
  },
  implicationsIntro: {
    marginBottom: '30px',
    fontSize: '1.1rem',
    color: '#333',
    lineHeight: 1.7,
  },
  implicationsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '20px',
  },
  implicationCard: {
    textAlign: 'center',
    padding: '20px',
    backgroundColor: '#f0f8ff',
    borderRadius: '8px',
    transition: 'transform 0.3s ease',
  },
  implicationIcon: {
    fontSize: '2.5rem',
    marginBottom: '15px',
  },
  implicationCard: {
    'h4': {
      color: '#008D36',
      fontSize: '1.1rem',
      marginBottom: '10px',
      fontWeight: '600',
    },
    'p': {
      color: '#666',
      fontSize: '0.9rem',
      lineHeight: 1.5,
    },
  },
};

export default MissionCommunicationPage;